import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export interface IncompletLesson {
  lesson_id: string;
  lesson_name: string;
  course_name: string;
  lesson_order: number;
  missing_fields: string[];
  missing_count: number;
}

export interface ContentDraft {
  id: string;
  lesson_id: string | null;
  course_id: string | null;
  field_type: string;
  draft_content: Record<string, unknown>;
  published_content: Record<string, unknown> | null;
  status: "draft" | "pending_review" | "approved" | "rejected" | "published";
  model_used: string;
  prompt_version: string;
  triggered_by: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  reviewer_notes: string | null;
  human_edited: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  ai_eval_results?: EvalResult[];
}

export interface EvalResult {
  id: string;
  schema_valid: boolean;
  tone_valid: boolean;
  completeness_valid: boolean;
  issues: string[];
  overall_score: number;
  auto_rejected: boolean;
}

export interface GenerateResult {
  draft_id: string;
  content: Record<string, unknown>;
  status: string;
  evaluation: {
    schemaValid: boolean;
    toneValid: boolean;
    completenessValid: boolean;
    issues: string[];
    overallScore: number;
  };
  model: string;
  prompt_version: string;
  latency_ms: number;
}

export function useAIContentStudio() {
  const [loading, setLoading] = useState(false);

  const invoke = useCallback(async (body: Record<string, unknown>) => {
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("ai-content-studio", { body });
      if (error) {
        console.error("AI Content Studio error:", error);
        toast.error("AI Content Studio error");
        return null;
      }
      if (data?.error) {
        toast.error(data.error);
        return null;
      }
      return data;
    } catch (err) {
      console.error("AI Content Studio failed:", err);
      toast.error("Failed to reach AI Content Studio");
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  const listIncomplete = useCallback(async (): Promise<IncompletLesson[]> => {
    const data = await invoke({ action: "list_incomplete" });
    return data?.lessons || [];
  }, [invoke]);

  const listDrafts = useCallback(async (filters?: { status?: string; lesson_id?: string }): Promise<ContentDraft[]> => {
    const data = await invoke({ action: "list_drafts", ...filters });
    return data?.drafts || [];
  }, [invoke]);

  const generateField = useCallback(async (
    fieldType: string,
    lessonId: string,
    lessonContext: Record<string, unknown>
  ): Promise<GenerateResult | null> => {
    const data = await invoke({
      action: "generate",
      field_type: fieldType,
      lesson_id: lessonId,
      lesson_context: lessonContext,
    });
    return data;
  }, [invoke]);

  const regenerateField = useCallback(async (
    fieldType: string,
    lessonId: string,
    lessonContext: Record<string, unknown>
  ): Promise<GenerateResult | null> => {
    const data = await invoke({
      action: "regenerate",
      field_type: fieldType,
      lesson_id: lessonId,
      lesson_context: lessonContext,
    });
    return data;
  }, [invoke]);

  const approveDraft = useCallback(async (draftId: string, notes?: string) => {
    const data = await invoke({ action: "approve", draft_id: draftId, notes });
    if (data?.success) toast.success("Draft approved");
    return data?.success || false;
  }, [invoke]);

  const rejectDraft = useCallback(async (draftId: string, notes?: string) => {
    const data = await invoke({ action: "reject", draft_id: draftId, notes });
    if (data?.success) toast.success("Draft rejected");
    return data?.success || false;
  }, [invoke]);

  const publishDraft = useCallback(async (draftId: string) => {
    const data = await invoke({ action: "publish", draft_id: draftId });
    if (data?.success) toast.success("Draft published");
    return data?.success || false;
  }, [invoke]);

  const batchAgeVariants = useCallback(async (
    lessonId: string,
    lessonContext: Record<string, unknown>
  ) => {
    const data = await invoke({
      action: "batch_age_variants",
      lesson_id: lessonId,
      lesson_context: lessonContext,
    });
    return data;
  }, [invoke]);

  const refreshExample = useCallback(async (
    lessonId: string,
    lessonContext: Record<string, unknown>
  ) => {
    const data = await invoke({
      action: "refresh_example",
      lesson_id: lessonId,
      lesson_context: lessonContext,
    });
    return data;
  }, [invoke]);

  return {
    loading,
    listIncomplete,
    listDrafts,
    generateField,
    regenerateField,
    approveDraft,
    rejectDraft,
    publishDraft,
    batchAgeVariants,
    refreshExample,
  };
}
